import axios, { AxiosRequestConfig } from 'axios'
import { getToken } from '@/global.config'
import type {
    WaAccountItem,
    WaConfigReq,
    WaCreateReq,
    WaEnvelope,
    WaGroupPage,
    WaQr,
} from './types'

/**
 * WhatsApp 个人号账号管理接口。
 *
 * 走 Next 网关（同源 /chatbot-api），只带当前用户的网关会话 token。
 * chatbot 的 management key 只在网关的密钥配置里，由网关按会话绑定公司、
 * 校验账号归属、过滤列表；前端不持有也不发送任何管理密钥。
 * 使用独立的 axios 实例，保留本模块自己的错误解析与超时。
 */
const PREFIX = '/chatbot-api/api/channel/whatsapp-personal/accounts'

const client = axios.create({
    baseURL: PREFIX,
    timeout: 80_000,
    headers: {
        Accept: 'application/json',
    },
})

export class WaApiError extends Error {
    status?: number
    code?: number
    requestId?: string
    /** 请求超时 / 网络中断 / 502：远端操作可能已经执行，需要先查询再重试 */
    uncertain: boolean
    canceled: boolean

    constructor(message: string, opts: Partial<WaApiError> = {}) {
        super(message)
        this.name = 'WaApiError'
        this.status = opts.status
        this.code = opts.code
        this.requestId = opts.requestId
        this.uncertain = !!opts.uncertain
        this.canceled = !!opts.canceled
    }
}

const ERROR_TEXT: Record<number, string> = {
    313300: 'Invalid request parameters',
    313301: 'WhatsApp service is not configured, please contact the administrator',
    313304: 'Account not found',
    313309: 'Account state conflict (creating / unbinding / unbound), please refresh',
    313400: 'Server database error',
    313402: 'Upstream service error, the operation may have completed, please refresh and retry',
    313403: 'Service unavailable, please retry later',
}

async function waRequest<T>(config: AxiosRequestConfig): Promise<T> {
    try {
        const res = await client.request<WaEnvelope<T>>({
            ...config,
            headers: {
                ...(config.headers || {}),
                ...(config.data === undefined ? {} : { 'Content-Type': 'application/json' }),
                ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
                'Cache-Control': 'no-store',
            },
        })
        const body = res.data
        if (res.status !== 200 || !body || typeof body !== 'object' || body.code !== 0) {
            throw new WaApiError(body?.message || 'WhatsApp account operation failed', {
                status: res.status,
                code: body?.code,
                requestId: body?.request_id,
            })
        }
        return body.data as T
    } catch (err: any) {
        if (err instanceof WaApiError) throw err
        if (axios.isCancel(err)) {
            throw new WaApiError('canceled', { canceled: true })
        }
        const status: number | undefined = err?.response?.status
        const body = err?.response?.data
        const code: number | undefined = body && typeof body === 'object' ? body.code : undefined
        const message =
            (code && ERROR_TEXT[code]) ||
            (body && typeof body === 'object' && body.message) ||
            (err?.code === 'ECONNABORTED' ? 'Request timeout, the result is unknown, please refresh' : '') ||
            err?.message ||
            'WhatsApp account operation failed'
        throw new WaApiError(message, {
            status,
            code,
            requestId: body && typeof body === 'object' ? body.request_id : undefined,
            uncertain: !status || status === 502 || status === 504 || err?.code === 'ECONNABORTED',
        })
    }
}

const path = (channelId: string, suffix = '') => `/${encodeURIComponent(channelId)}${suffix}`

/** 01 查询账号列表（网关已按当前公司过滤） */
export const listWaAccounts = (signal?: AbortSignal) =>
    waRequest<WaAccountItem[]>({ url: '', method: 'get', signal })

/** 02 创建账号并开始登录（POST 已启动 session，不要再无条件 login） */
export const createWaAccount = (data: WaCreateReq, signal?: AbortSignal) =>
    waRequest<WaAccountItem>({ url: '', method: 'post', data, signal })

/** 03 查询账号详情与连接状态 */
export const getWaAccount = (channelId: string, signal?: AbortSignal) =>
    waRequest<WaAccountItem>({ url: path(channelId), method: 'get', signal })

/** 04 启动或恢复账号登录（仅用户点击时调用，不要轮询） */
export const loginWaAccount = (channelId: string, signal?: AbortSignal) =>
    waRequest<WaAccountItem>({ url: path(channelId, '/login'), method: 'post', signal })

/** 05 获取当前扫码二维码，仅 SCAN_QR_CODE 时调用 */
export const getWaQr = (channelId: string, signal?: AbortSignal) =>
    waRequest<WaQr>({ url: path(channelId, '/qr'), method: 'get', signal })

/** 06 获取账号加入的群（limit 1–100，无 total） */
export const getWaGroups = (channelId: string, limit = 50, offset = 0, signal?: AbortSignal) =>
    waRequest<WaGroupPage>({ url: path(channelId, '/groups'), method: 'get', params: { limit, offset }, signal })

/** 07 保存群白名单与机器人配置（group_ids 全量替换） */
export const updateWaConfig = (channelId: string, data: WaConfigReq, signal?: AbortSignal) =>
    waRequest<WaAccountItem>({ url: path(channelId, '/config'), method: 'put', data, signal })

/** 08 统一解绑并退出关联设备 */
export const unbindWaAccount = (channelId: string, signal?: AbortSignal) =>
    waRequest<WaAccountItem>({ url: path(channelId), method: 'delete', signal })

/** 一次新增操作生成一个稳定 channel_id（小写字母数字，≤64） */
export const genWaChannelId = () => {
    const raw =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`
    return `wa_${raw.replace(/-/g, '').toLowerCase()}`.slice(0, 64)
}

export const waAccountStatusText = (status?: string | null) => {
    switch (status) {
        case 'STARTING':
        case 'PENDING':
            return 'Connecting'
        case 'SCAN_QR_CODE':
            return 'Waiting for scan'
        case 'WORKING':
            return 'Connected'
        case 'STOPPED':
            return 'Stopped'
        case 'FAILED':
            return 'Failed'
        case 'DELETING':
            return 'Unbinding'
        case 'DELETED':
            return 'Unbound'
        default:
            return status || 'Unknown'
    }
}
